import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Link2, X, Loader2, Image as ImageIcon, Video } from "lucide-react";
import { api } from "../lib/api";
import { mediaUrl, isYouTube, isVimeo, isVideoFile, toEmbed } from "../lib/media";

/**
 * MediaPicker
 * - Drag & drop file upload from PC
 * - Or paste any URL (YouTube, Vimeo, direct image/video, /api/uploads/*)
 * - Live preview of current value
 *
 * Props:
 *  value: string (current URL)
 *  onChange: (url: string) => void
 *  accept: "image" | "video" | "both" (default "both")
 *  label?: string
 *  compact?: boolean
 */
export default function MediaPicker({ value, onChange, accept = "both", label, compact = false }) {
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileRef = useRef(null);

  const acceptAttr =
    accept === "image"
      ? "image/*"
      : accept === "video"
      ? "video/*"
      : "image/*,video/*";

  const uploadFile = useCallback(
    async (file) => {
      if (!file) return;
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onChange(data.url);
        toast.success(`Yüklendi: ${data.original_name}`);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Yükleme başarısız");
      } finally {
        setBusy(false);
      }
    },
    [onChange],
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);
      const f = e.dataTransfer?.files?.[0];
      if (f) uploadFile(f);
    },
    [uploadFile],
  );

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) uploadFile(f);
    e.target.value = ""; // allow re-picking same file
  };

  const applyUrl = () => {
    if (urlInput.trim()) {
      onChange(urlInput.trim());
      toast.success("URL uygulandı");
      setUrlInput("");
      setShowUrlInput(false);
    }
  };

  const clear = () => onChange("");

  const displayUrl = mediaUrl(value);
  const isVid = value && (isYouTube(value) || isVimeo(value) || isVideoFile(value));
  const embed = toEmbed(value);

  return (
    <div className="rounded-lg border border-white/10 bg-[#0A0A11] overflow-hidden">
      {label && (
        <div className="px-3 pt-2 text-[10px] tracking-widest uppercase text-white/50 font-mono-ui">
          {label}
        </div>
      )}

      {/* Preview */}
      {value ? (
        <div className="relative m-3 rounded-md overflow-hidden bg-black" style={{ aspectRatio: compact ? "16 / 9" : "16 / 10" }}>
          {isYouTube(value) || isVimeo(value) ? (
            <iframe src={embed} title="preview" className="w-full h-full" allowFullScreen />
          ) : isVideoFile(value) ? (
            <video src={displayUrl} controls className="w-full h-full object-cover bg-black" />
          ) : (
            <img src={displayUrl} alt="" className="w-full h-full object-cover" />
          )}
          <button
            type="button"
            onClick={clear}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500"
            title="Kaldır"
          >
            <X size={14} />
          </button>
          <div className="absolute bottom-2 left-2 px-2 py-1 rounded bg-black/70 text-[10px] tracking-widest uppercase text-white/80 font-mono-ui flex items-center gap-1">
            {isVid ? <Video size={10} /> : <ImageIcon size={10} />}
            {isYouTube(value) ? "YouTube" : isVimeo(value) ? "Vimeo" : value.startsWith("/api/uploads/") ? "Yüklendi" : "URL"}
          </div>
        </div>
      ) : (
        <div
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onClick={() => fileRef.current?.click()}
          className={`m-3 rounded-md border-2 border-dashed cursor-pointer transition-colors ${
            dragging ? "border-[#A855F7] bg-[#A855F7]/5" : "border-white/15 hover:border-white/30"
          } flex flex-col items-center justify-center py-8 text-center`}
        >
          {busy ? (
            <Loader2 size={22} className="animate-spin text-[#A855F7]" />
          ) : (
            <>
              <Upload size={20} className="text-white/50 mb-2" />
              <div className="text-white/70 text-sm">Dosyayı buraya bırak</div>
              <div className="text-white/40 text-xs mt-1">veya tıklayıp seç · maks. 200 MB</div>
            </>
          )}
        </div>
      )}

      {/* URL input */}
      <div className="px-3 pb-3">
        {showUrlInput ? (
          <div className="flex gap-2 items-center">
            <input
              type="text"
              autoFocus
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyUrl()}
              placeholder="YouTube / Vimeo / URL yapıştır"
              className="flex-1 bg-[#08080D] border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-[#A855F7]"
              data-testid="media-url-input"
            />
            <button
              type="button"
              onClick={applyUrl}
              className="btn-primary text-[10px] py-2 px-3"
              data-testid="media-url-apply"
            >
              Uygula
            </button>
            <button type="button" onClick={() => setShowUrlInput(false)} className="text-white/50 hover:text-white px-2">
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/10 text-white/70 hover:text-white hover:border-white/30 text-xs disabled:opacity-50"
              data-testid="media-pick-file"
            >
              <Upload size={12} /> Dosya Seç
            </button>
            <button
              type="button"
              onClick={() => { setShowUrlInput(true); setUrlInput(value || ""); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/10 text-white/70 hover:text-white hover:border-white/30 text-xs"
              data-testid="media-pick-url"
            >
              <Link2 size={12} /> URL Kullan
            </button>
          </div>
        )}
        <input ref={fileRef} type="file" accept={acceptAttr} onChange={onPick} className="hidden" />
      </div>
    </div>
  );
}
